import { useState } from "react";

/* ═══════════════════════════════════════════════════════════════════════════
   MAGHIL ANALYTICS — CHECK-IN REPORT — MOBILE (with Drill-Ins)
   Design system: Maghil Analytics Design Tokens v1
   Layout: 375px mobile reference with drill-in navigation
   ═══════════════════════════════════════════════════════════════════════════ */

const T = {
  bg: "#F5F6FA", surface: "#FFFFFF", card: "#FFFFFF", hover: "#F0F1F5",
  accent: "#D23A3B", accentSoft: "rgba(210,58,59,0.08)", accentGlow: "rgba(210,58,59,0.15)",
  green: "#16A34A", greenSoft: "rgba(22,163,74,0.08)",
  red: "#DC2626", redSoft: "rgba(220,38,38,0.08)",
  orange: "#D97706", orangeSoft: "rgba(217,119,6,0.08)",
  t1: "#1A1D2E", t2: "#5A6178", t3: "#9CA3B8",
  cardBorder: "#E2E5EC", divider: "#E8EAF0",
  shadow: "0 1px 3px rgba(0,0,0,0.06)",
  font: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

/* ═══════════════════════════════════════════════════════════════════════════
   MOCK DATA
   ═══════════════════════════════════════════════════════════════════════════ */
const MOCK = {
  kpis: [
    { key: "checkins",    label: "TOTAL CHECK-INS",   value: "284",   delta: 8.3 },
    { key: "guests",      label: "TOTAL GUESTS",      value: "712",   delta: 5.1 },
    { key: "avgParty",    label: "AVG PARTY SIZE",    value: "2.5",   delta: -0.2 },
    { key: "cancels",     label: "CANCEL RATE",       value: "12.3%", delta: -2.1, invertDelta: true },
    { key: "waitTime",    label: "AVG WAIT TIME",     value: "18m",   delta: -3.4, invertDelta: true },
    { key: "dinein",      label: "AVG DINE-IN",       value: "47m",   delta: 1.2 },
    { key: "peakHour",    label: "PEAK HOUR",         value: "7 PM",  delta: null },
    { key: "totalCancel", label: "TOTAL CANCELS",     value: "35",    delta: -6.2, invertDelta: true },
    { key: "seatedEst",   label: "SEATED RATE*",      value: "87.7%", delta: 2.1 },
  ],
  hourly: [
    { h: "10A", v: 8 },  { h: "11A", v: 22 }, { h: "12P", v: 41 },
    { h: "1P",  v: 38 }, { h: "2P",  v: 19 }, { h: "3P",  v: 10 },
    { h: "4P",  v: 7 },  { h: "5P",  v: 25 }, { h: "6P",  v: 44 },
    { h: "7P",  v: 52 }, { h: "8P",  v: 46 }, { h: "9P",  v: 30 },
    { h: "10P", v: 14 },
  ],
  partySize: [
    { size: "1",  count: 18, wait: 8,  dinein: 32, guests: 18 },
    { size: "2",  count: 94, wait: 14, dinein: 42, guests: 188 },
    { size: "3",  count: 52, wait: 18, dinein: 48, guests: 156 },
    { size: "4",  count: 68, wait: 22, dinein: 55, guests: 272 },
    { size: "5",  count: 26, wait: 28, dinein: 62, guests: 130 },
    { size: "6",  count: 16, wait: 34, dinein: 68, guests: 96 },
    { size: "7+", count: 10, wait: 42, dinein: 78, guests: 80 },
  ],
  channels: [
    { name: "Host Added", pct: 50, count: 142, color: T.accent },
    { name: "Online",     pct: 22, count: 62,  color: T.green },
    { name: "Kiosk",      pct: 16, count: 45,  color: T.orange },
    { name: "Google",     pct: 12, count: 35,  color: "#6366F1" },
  ],
  cancelTrend: [
    { d: "M", cancels: 4,  rate: 9.1 },
    { d: "T", cancels: 6,  rate: 11.5 },
    { d: "W", cancels: 5,  rate: 10.2 },
    { d: "T", cancels: 7,  rate: 13.0 },
    { d: "F", cancels: 10, rate: 14.1 },
    { d: "S", cancels: 12, rate: 15.4 },
    { d: "S", cancels: 8,  rate: 12.8 },
  ],
  heatmap: (() => {
    const days = ["M","T","W","T","F","S","S"];
    const hours = [11,12,13,17,18,19,20,21,22];
    const data = [
      [5,12,10,6,14,18,16,8,3],[6,14,12,7,16,20,18,10,4],[5,11,9,5,13,17,15,7,3],
      [7,15,13,8,18,22,20,12,5],[10,20,18,12,24,30,28,16,8],[12,24,22,14,28,36,32,20,10],
      [8,18,16,10,20,26,22,14,6],
    ];
    return { days, hours, data };
  })(),
  dailyCheckins: [
    { d: "Mon", checkins: 34, guests: 82 },{ d: "Tue", checkins: 38, guests: 90 },
    { d: "Wed", checkins: 36, guests: 84 },{ d: "Thu", checkins: 42, guests: 106 },
    { d: "Fri", checkins: 52, guests: 132 },{ d: "Sat", checkins: 56, guests: 142 },
    { d: "Sun", checkins: 48, guests: 120 },
  ],
  waitByDay: [
    { d: "Mon", wait: 14 },{ d: "Tue", wait: 16 },{ d: "Wed", wait: 15 },
    { d: "Thu", wait: 18 },{ d: "Fri", wait: 24 },{ d: "Sat", wait: 28 },{ d: "Sun", wait: 20 },
  ],
  dineinByDay: [
    { d: "Mon", dur: 42 },{ d: "Tue", dur: 44 },{ d: "Wed", dur: 43 },
    { d: "Thu", dur: 46 },{ d: "Fri", dur: 52 },{ d: "Sat", dur: 55 },{ d: "Sun", dur: 48 },
  ],
  cancelByChannel: [
    { name: "Host Added", cancels: 14, rate: 9.9 },
    { name: "Online",     cancels: 10, rate: 16.1 },
    { name: "Kiosk",      cancels: 7,  rate: 15.6 },
    { name: "Google",     cancels: 4,  rate: 11.4 },
  ],
  cancelByHour: [
    { h: "11A", c: 1 },{ h: "12P", c: 3 },{ h: "1P", c: 2 },
    { h: "5P", c: 2 },{ h: "6P", c: 5 },{ h: "7P", c: 8 },
    { h: "8P", c: 6 },{ h: "9P", c: 4 },{ h: "10P", c: 2 },
  ],
  hourlySummary: [
    { hour: "7 PM", checkins: 12, guests: 31, cancels: 2, dinein: 48 },
    { hour: "6 PM", checkins: 14, guests: 35, cancels: 1, dinein: 52 },
    { hour: "5 PM", checkins: 8,  guests: 19, cancels: 1, dinein: 44 },
    { hour: "4 PM", checkins: 3,  guests: 7,  cancels: 0, dinein: 38 },
    { hour: "3 PM", checkins: 4,  guests: 9,  cancels: 0, dinein: 36 },
    { hour: "2 PM", checkins: 7,  guests: 16, cancels: 1, dinein: 42 },
  ],
  guestVolumeTrend: [
    { d: "2/9",  guests: 462, checkins: 138 },{ d: "2/10", guests: 440, checkins: 130 },
    { d: "2/11", guests: 478, checkins: 144 },{ d: "2/12", guests: 455, checkins: 136 },
    { d: "2/13", guests: 430, checkins: 126 },{ d: "2/14", guests: 390, checkins: 110 },
    { d: "2/15", guests: 465, checkins: 140 },{ d: "2/16", guests: 448, checkins: 132 },
    { d: "2/17", guests: 420, checkins: 122 },{ d: "2/18", guests: 358, checkins: 102 },
    { d: "2/19", guests: 410, checkins: 118 },{ d: "2/20", guests: 442, checkins: 130 },
    { d: "2/21", guests: 468, checkins: 142 },{ d: "2/22", guests: 380, checkins: 108 },
    { d: "2/23", guests: 395, checkins: 114 },{ d: "2/24", guests: 370, checkins: 105 },
    { d: "2/25", guests: 408, checkins: 120 },{ d: "2/26", guests: 415, checkins: 122 },
    { d: "2/27", guests: 388, checkins: 112 },{ d: "2/28", guests: 365, checkins: 100 },
    { d: "3/1",  guests: 490, checkins: 148 },{ d: "3/2",  guests: 510, checkins: 155 },
    { d: "3/3",  guests: 485, checkins: 146 },{ d: "3/4",  guests: 468, checkins: 140 },
    { d: "3/5",  guests: 445, checkins: 132 },{ d: "3/6",  guests: 470, checkins: 142 },
    { d: "3/7",  guests: 505, checkins: 152 },{ d: "3/8",  guests: 460, checkins: 136 },
    { d: "3/9",  guests: 480, checkins: 145 },{ d: "3/10", guests: 465, checkins: 138 },
  ],
};

const DRILLABLE = ["checkins","guests","avgParty","cancels","waitTime","dinein"];

/* ═══════════════════════════════════════════════════════════════════════════
   SHARED UI COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */
const TABS = [
  { label: "Snapshot", icon: "flash_on", active: false },
  { label: "Reports",  icon: "bar_chart", active: true },
  { label: "Brisque AI",icon: "auto_awesome", active: false },
  { label: "Support",  icon: "chat_bubble", active: false },
];

function StatusBar() {
  return (
    <div style={{ height: 44, background: T.bg, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", fontSize: 12, fontWeight: 600, color: T.t1, fontFamily: T.font }}>
      <span>9:41</span>
      <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
        <svg width="16" height="11" viewBox="0 0 16 11"><path d="M1 7.5h2v3H1zm3.5-2.5h2v5.5h-2zm3.5-2h2V11H8zm3.5-3h2v10.5h-2z" fill={T.t1}/></svg>
        <svg width="14" height="11" viewBox="0 0 14 11"><path d="M7 2.8A6.2 6.2 0 0 0 2 5l1 1.2a4.6 4.6 0 0 1 8 0L12 5A6.2 6.2 0 0 0 7 2.8zM7 6.2A3.2 3.2 0 0 0 4.8 7.4L7 10l2.2-2.6A3.2 3.2 0 0 0 7 6.2z" fill={T.t1}/></svg>
        <svg width="22" height="11" viewBox="0 0 22 11"><rect x="0" y="1" width="19" height="9" rx="2" stroke={T.t1} strokeWidth="1" fill="none"/><rect x="20" y="3.5" width="1.5" height="4" rx="0.5" fill={T.t1}/><rect x="1.5" y="2.5" width="14" height="6" rx="1" fill={T.t1}/></svg>
      </div>
    </div>
  );
}

function BottomNav() {
  return (
    <div style={{ position: "sticky", bottom: 0, height: 72, background: T.surface, borderTop: `1px solid ${T.divider}`, display: "flex", justifyContent: "space-around", alignItems: "center", paddingBottom: 8 }}>
      {TABS.map((t, i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "4px 14px", cursor: "pointer" }}>
          <span className="material-symbols-rounded" style={{ fontSize: 22, color: t.active ? T.accent : T.t3, opacity: t.active ? 1 : 0.4 }}>{t.icon}</span>
          <span style={{ fontSize: 9.5, fontWeight: t.active ? 700 : 400, color: t.active ? T.accent : T.t3 }}>{t.label}</span>
          {t.active && <span style={{ width: 4, height: 4, borderRadius: "50%", background: T.accent, marginTop: 1 }}/>}
        </div>
      ))}
    </div>
  );
}

function DrillInHeader({ title, subtitle, breadcrumb, onBack }: any) {
  return (
    <div style={{ background: T.bg, padding: "8px 20px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div onClick={onBack} style={{ width: 28, height: 28, borderRadius: 8, background: T.surface, border: `1px solid ${T.divider}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t3 }}>chevron_left</span>
        </div>
        <span style={{ fontSize: 11, color: T.t3 }}>{breadcrumb}</span>
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: T.t1, marginBottom: 2 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 11, color: T.t3, marginBottom: 8 }}>{subtitle}</div>}
    </div>
  );
}

function SummaryKPIs({ items }: any) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(items.length, 3)}, 1fr)`, gap: 8, marginBottom: 4 }}>
      {items.map((it, i) => (
        <div key={i} style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: "10px 8px", boxShadow: T.shadow, textAlign: "center" }}>
          <div style={{ fontSize: 7, fontWeight: 400, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>{it.label}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: it.color || T.t1, marginTop: 3 }}>{it.value}</div>
        </div>
      ))}
    </div>
  );
}

function MCard({ title, subtitle, children, drillable, onTap }: any) {
  return (
    <div onClick={onTap} style={{ background: T.card, border: `1px solid ${drillable ? `${T.accent}33` : T.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: T.shadow, position: "relative", cursor: onTap ? "pointer" : "default" }}>
      {drillable && <span className="material-symbols-rounded" style={{ position: "absolute", top: 10, right: 10, fontSize: 14, color: T.accent, opacity: 0.45 }}>chevron_right</span>}
      <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 9, color: T.t3, marginTop: 1, marginBottom: 10 }}>{subtitle}</div>}
      {!subtitle && <div style={{ marginBottom: 10 }}/>}
      {children}
    </div>
  );
}

function BarChart({ data, valueKey, labelKey, height = 80, color = T.accent, threshold }: any) {
  const maxV = Math.max(...data.map(d => d[valueKey]));
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height }}>
      {data.map((d, i) => {
        const v = d[valueKey];
        const h = maxV > 0 ? (v / maxV) * (height - 16) : 0;
        const isPeak = v === maxV;
        const over = threshold && v > threshold;
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ fontSize: 7, fontWeight: 600, color: over ? T.accent : isPeak ? color : T.t3, marginBottom: 2 }}>{v}</div>
            <div style={{ width: "100%", maxWidth: 18, height: h, borderRadius: "3px 3px 0 0", background: over ? T.accent : isPeak ? color : `${color}66` }}/>
            <div style={{ fontSize: 7, color: isPeak ? T.t1 : T.t3, fontWeight: isPeak ? 700 : 400, marginTop: 3 }}>{d[labelKey]}</div>
          </div>
        );
      })}
    </div>
  );
}

function HProgressList({ data, labelKey, valueKey, valueSuffix = "", color = T.accent }: any) {
  const maxV = Math.max(...data.map(d => d[valueKey]));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {data.map((d, i) => (
        <div key={i}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
            <span style={{ fontSize: 11, color: T.t2 }}>{d[labelKey]}</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.t1 }}>{d[valueKey]}{valueSuffix}</span>
          </div>
          <div style={{ height: 5, background: T.hover, borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${maxV > 0 ? (d[valueKey] / maxV) * 100 : 0}%`, borderRadius: 3, background: `${color}AA` }}/>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   GUEST VOLUME TREND — Dual-line SVG chart (mobile)
   ═══════════════════════════════════════════════════════════════════════════ */
function GuestVolumeTrendMobile() {
  const data = MOCK.guestVolumeTrend;
  const n = data.length;
  const allVals = [...data.map(d => d.guests), ...data.map(d => d.checkins)];
  const maxV = Math.max(...allVals);
  const w = 343; const h = 140;
  const pad = { top: 6, right: 8, bottom: 22, left: 32 };
  const cW = w - pad.left - pad.right;
  const cH = h - pad.top - pad.bottom;
  const toX = (i: any) => pad.left + (i / (n - 1)) * cW;
  const toY = (v: any) => pad.top + cH - (v / maxV) * cH;

  const gLine = data.map((d, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)},${toY(d.guests).toFixed(1)}`).join(" ");
  const cLine = data.map((d, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)},${toY(d.checkins).toFixed(1)}`).join(" ");
  const area = gLine + ` L${toX(n-1).toFixed(1)},${(pad.top + cH).toFixed(1)} L${toX(0).toFixed(1)},${(pad.top + cH).toFixed(1)} Z`;
  const yTicks = [0, 150, 300, 450, 600].filter(v => v <= maxV + 50);
  const xIdx = data.map((_, i) => i).filter(i => i % 7 === 0 || i === n - 1);

  return (
    <MCard title="Guest Volume Trend" subtitle="Daily guests & check-ins" drillable onTap={undefined}>
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ display: "block" }}>
        <defs>
          <linearGradient id="gAreaM" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={T.accent} stopOpacity="0.15"/>
            <stop offset="100%" stopColor={T.accent} stopOpacity="0.01"/>
          </linearGradient>
        </defs>
        {yTicks.map(v => (
          <g key={v}>
            <line x1={pad.left} y1={toY(v)} x2={w - pad.right} y2={toY(v)} stroke={T.divider} strokeWidth="0.5"/>
            <text x={pad.left - 4} y={toY(v) + 3} textAnchor="end" fontSize="7" fill={T.t3} fontFamily={T.font}>{v}</text>
          </g>
        ))}
        <path d={area} fill="url(#gAreaM)"/>
        <path d={gLine} fill="none" stroke={T.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d={cLine} fill="none" stroke={T.green} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="4 3"/>
        <circle cx={toX(n-1)} cy={toY(data[n-1].guests)} r="3" fill={T.accent} stroke="#fff" strokeWidth="1.5"/>
        <circle cx={toX(n-1)} cy={toY(data[n-1].checkins)} r="2.5" fill={T.green} stroke="#fff" strokeWidth="1.5"/>
        {xIdx.map(i => (
          <text key={i} x={toX(i)} y={h - 4} textAnchor="middle" fontSize="7" fill={T.t3} fontFamily={T.font}>{data[i].d}</text>
        ))}
      </svg>
      <div style={{ display: "flex", gap: 14, marginTop: 6, justifyContent: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ width: 14, height: 2.5, background: T.accent, borderRadius: 1 }}/>
          <span style={{ fontSize: 9, color: T.t2 }}>Guests</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <svg width="14" height="3"><line x1="0" y1="1.5" x2="14" y2="1.5" stroke={T.green} strokeWidth="1.5" strokeDasharray="3 2"/></svg>
          <span style={{ fontSize: 9, color: T.t2 }}>Check-Ins</span>
        </div>
      </div>
    </MCard>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   HOURLY SUMMARY CHART — Mobile horizontal grouped bars
   ═══════════════════════════════════════════════════════════════════════════ */
function HourlySummaryChartMobile() {
  const raw = MOCK.hourlySummary;
  const data = [...raw].reverse();
  const maxG = Math.max(...data.map(d => d.guests));

  return (
    <MCard title="Hourly Summary" subtitle="Guests, check-ins & cancellations by hour">
      {/* Legend */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        {[
          { color: T.accent, label: "Guests" },
          { color: T.green, label: "Check-Ins" },
          { color: T.red, label: "Cancels" },
          { color: T.orange, label: "Dine-In" },
        ].map((l, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <div style={{ width: 6, height: 6, borderRadius: 2, background: l.color }}/>
            <span style={{ fontSize: 7, color: T.t2 }}>{l.label}</span>
          </div>
        ))}
      </div>

      {/* Chart rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {data.map((d, i) => {
          const gPct = (d.guests / maxG) * 100;
          const cPct = (d.checkins / maxG) * 100;
          const caPct = d.cancels > 0 ? Math.max((d.cancels / maxG) * 100, 3) : 0;
          return (
            <div key={i}>
              {/* Hour + Dine-in header row */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: T.t1 }}>{d.hour}</span>
                <span style={{ fontSize: 8, fontWeight: 500, color: T.orange }}>{d.dinein} min</span>
              </div>
              {/* Guests bar */}
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 2 }}>
                <div style={{ flex: 1, height: 8, background: T.hover, borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ width: `${gPct}%`, height: "100%", borderRadius: 3, background: `${T.accent}CC` }}/>
                </div>
                <span style={{ fontSize: 7, fontWeight: 600, color: T.accent, width: 20, textAlign: "right", flexShrink: 0 }}>{d.guests}</span>
              </div>
              {/* Check-ins + Cancels bar */}
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <div style={{ flex: 1, height: 6, display: "flex", gap: 2 }}>
                  <div style={{ width: `${cPct}%`, height: "100%", borderRadius: 3, background: `${T.green}BB` }}/>
                  {d.cancels > 0 && <div style={{ width: `${caPct}%`, height: "100%", borderRadius: 3, background: `${T.red}BB` }}/>}
                </div>
                <span style={{ fontSize: 7, fontWeight: 600, color: T.green, width: 20, textAlign: "right", flexShrink: 0 }}>
                  {d.checkins}{d.cancels > 0 && <span style={{ color: T.red, marginLeft: 2 }}>· {d.cancels}</span>}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </MCard>
  );
}

function KPICard({ label, value, delta, invertDelta, drillable, onTap }: any) {
  const isPositive = invertDelta ? delta < 0 : delta > 0;
  const deltaColor = delta === null ? T.t3 : isPositive ? T.green : T.red;
  const deltaText = delta === null ? "" : `${isPositive ? "↑" : "↓"} ${Math.abs(delta).toFixed(1)}%`;
  const hasDelta = delta !== null;
  return (
    <div onClick={onTap} style={{ background: T.card, border: `1px solid ${drillable ? `${T.accent}33` : T.cardBorder}`, borderRadius: hasDelta ? 12 : 10, padding: hasDelta ? "12px 10px" : "10px 10px", boxShadow: T.shadow, textAlign: "center", position: "relative", cursor: drillable ? "pointer" : "default" }}>
      {drillable && <span className="material-symbols-rounded" style={{ position: "absolute", top: 5, right: 5, fontSize: 12, color: T.accent, opacity: 0.45 }}>chevron_right</span>}
      <div style={{ fontSize: 8, fontWeight: 400, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: T.t1, marginTop: hasDelta ? 4 : 3 }}>{value}</div>
      {hasDelta && <div style={{ fontSize: 9, fontWeight: 600, color: deltaColor, marginTop: 3 }}>{deltaText}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN DASHBOARD
   ═══════════════════════════════════════════════════════════════════════════ */
function MainDashboard({ onDrill }: any) {
  const { days, hours, data: heatData } = MOCK.heatmap;
  const maxHeat = Math.max(...heatData.flat());
  const hourLabels = hours.map(h => h > 12 ? `${h-12}P` : h === 12 ? "12P" : `${h}A`);

  return (
    <>
      <div style={{ background: T.bg, padding: "8px 20px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: T.surface, border: `1px solid ${T.divider}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t3 }}>chevron_left</span>
          </div>
          <span style={{ fontSize: 11, color: T.t3 }}>Reports › Guests</span>
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: T.t1, marginBottom: 4 }}>Check-In Analytics</div>
        <div style={{ fontSize: 11, color: T.t3, marginBottom: 10 }}>Walk-in waitlist & queue performance</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 4 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: T.surface, border: `1px solid ${T.divider}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span className="material-symbols-rounded" style={{ fontSize: 14, color: T.t3 }}>chevron_left</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.surface, border: `1px solid ${T.divider}`, borderRadius: 8, padding: "4px 12px" }}>
            <span className="material-symbols-rounded" style={{ fontSize: 14, color: T.t3 }}>calendar_today</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>Last 7 Days</div>
              <div style={{ fontSize: 10, color: T.t3 }}>Mar 3 – Mar 10, 2026</div>
            </div>
          </div>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: T.surface, border: `1px solid ${T.divider}`, display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.3 }}>
            <span className="material-symbols-rounded" style={{ fontSize: 14, color: T.t3 }}>chevron_right</span>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        {[0,3,6].map(s => (
          <div key={s} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {MOCK.kpis.slice(s, s+3).map(k => (
              <KPICard key={k.key} {...k} drillable={DRILLABLE.includes(k.key)} onTap={DRILLABLE.includes(k.key) ? () => onDrill(k.key) : undefined} />
            ))}
          </div>
        ))}
        <div style={{ fontSize: 8, color: T.t3, textAlign: "center", marginTop: -2 }}>* Seated Rate is derived: (Check-Ins − Cancellations) / Check-Ins</div>

        <GuestVolumeTrendMobile />

        <MCard title="Hourly Volume" subtitle="Parties per hour" drillable onTap={() => onDrill("checkins")}>
          <BarChart data={MOCK.hourly} valueKey="v" labelKey="h" height={80} color={T.accent} />
        </MCard>

        <MCard title="Party Sizes" subtitle="Check-ins by group size" drillable onTap={() => onDrill("avgParty")}>
          <BarChart data={MOCK.partySize} valueKey="count" labelKey="size" height={70} color={T.green} />
        </MCard>

        <MCard title="Wait by Party Size" subtitle="Avg wait (min) per group size" drillable onTap={() => onDrill("waitTime")}>
          <HProgressList data={MOCK.partySize.map(d => ({ label: `Party of ${d.size}`, wait: d.wait }))} labelKey="label" valueKey="wait" valueSuffix=" min" color={T.orange} />
        </MCard>

        <MCard title="Cancellation Trend" subtitle="Daily cancellations this week" drillable onTap={() => onDrill("cancels")}>
          <BarChart data={MOCK.cancelTrend.map(d => ({ d: d.d, v: d.cancels }))} valueKey="v" labelKey="d" height={60} color={T.red} />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, padding: "6px 10px", background: T.hover, borderRadius: 8 }}>
            <span style={{ fontSize: 10, color: T.t2 }}>Avg Cancel Rate</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: T.red }}>12.3%</span>
          </div>
        </MCard>

        <MCard title="Channel Mix" subtitle="Source of check-ins">
          <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", marginBottom: 10 }}>
            {MOCK.channels.map((c, i) => <div key={i} style={{ flex: c.pct, background: c.color }}/>)}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {MOCK.channels.map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <div style={{ width: 6, height: 6, borderRadius: 2, background: c.color }}/>
                <span style={{ fontSize: 9, color: T.t2 }}>{c.name}</span>
                <span style={{ fontSize: 9, fontWeight: 600, color: T.t1 }}>{c.pct}%</span>
              </div>
            ))}
          </div>
        </MCard>

        <MCard title="Volume Heatmap" subtitle="Hour × day — darker = busier">
          <div style={{ display: "flex" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, marginRight: 4, paddingTop: 16 }}>
              {days.map((d, i) => <div key={i} style={{ height: 16, display: "flex", alignItems: "center", fontSize: 7, color: T.t3, width: 14 }}>{d}</div>)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", gap: 2, marginBottom: 2 }}>
                {hourLabels.map((h, i) => <div key={i} style={{ flex: 1, textAlign: "center", fontSize: 7, color: T.t3 }}>{h}</div>)}
              </div>
              {days.map((_, di) => (
                <div key={di} style={{ display: "flex", gap: 2, marginBottom: 2 }}>
                  {hours.map((_, hi) => {
                    const v = heatData[di][hi]; const intensity = v / maxHeat;
                    return (
                      <div key={hi} style={{ flex: 1, height: 16, borderRadius: 2, background: `rgba(210,58,59,${0.06 + intensity * 0.59})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 6, fontWeight: intensity > 0.75 ? 600 : 400, color: intensity > 0.75 ? "#fff" : "transparent" }}>{v}</div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </MCard>

        <HourlySummaryChartMobile />
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DRILL-IN: TOTAL CHECK-INS — by day, by hour, by channel
   ═══════════════════════════════════════════════════════════════════════════ */
function DrillCheckins({ onBack }: any) {
  return (
    <>
      <DrillInHeader title="Total Check-Ins" subtitle="284 parties this period" breadcrumb="Check-In Analytics › Check-Ins" onBack={onBack} />
      <div style={{ flex: 1, overflow: "auto", padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        <SummaryKPIs items={[{ label: "THIS PERIOD", value: "284" },{ label: "PRIOR PERIOD", value: "262" },{ label: "CHANGE", value: "↑ 8.3%", color: T.green }]}/>
        <MCard title="Daily Check-In Volume" subtitle="Parties per day this week">
          <BarChart data={MOCK.dailyCheckins} valueKey="checkins" labelKey="d" height={80} color={T.accent} />
        </MCard>
        <MCard title="By Hour of Day" subtitle="When walk-ins arrive">
          <BarChart data={MOCK.hourly} valueKey="v" labelKey="h" height={70} color={T.accent} />
        </MCard>
        <MCard title="By Channel" subtitle="Check-in source breakdown">
          <HProgressList data={MOCK.channels.map(c => ({ name: c.name, count: c.count }))} labelKey="name" valueKey="count" color={T.accent} />
        </MCard>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DRILL-IN: TOTAL GUESTS — by day, by party size
   ═══════════════════════════════════════════════════════════════════════════ */
function DrillGuests({ onBack }: any) {
  return (
    <>
      <DrillInHeader title="Total Guests" subtitle="712 guests this period" breadcrumb="Check-In Analytics › Guests" onBack={onBack} />
      <div style={{ flex: 1, overflow: "auto", padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        <SummaryKPIs items={[{ label: "THIS PERIOD", value: "712" },{ label: "PRIOR PERIOD", value: "678" },{ label: "AVG / DAY", value: "102" }]}/>
        <GuestVolumeTrendMobile />
        <MCard title="Daily Guest Volume" subtitle="Total guests per day">
          <BarChart data={MOCK.dailyCheckins} valueKey="guests" labelKey="d" height={80} color={T.green} />
        </MCard>
        <MCard title="Guests by Party Size" subtitle="How many guests per group size">
          <HProgressList data={MOCK.partySize.map(d => ({ label: `Party of ${d.size}`, guests: d.guests }))} labelKey="label" valueKey="guests" color={T.green} />
        </MCard>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DRILL-IN: AVG PARTY SIZE — distribution, wait & dine-in by size
   ═══════════════════════════════════════════════════════════════════════════ */
function DrillAvgParty({ onBack }: any) {
  const data = MOCK.partySize;
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <>
      <DrillInHeader title="Avg Party Size" subtitle="2.5 guests per party" breadcrumb="Check-In Analytics › Party Size" onBack={onBack} />
      <div style={{ flex: 1, overflow: "auto", padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        <SummaryKPIs items={[{ label: "AVG SIZE", value: "2.5" },{ label: "MOST COMMON", value: "Party of 2" },{ label: "TOTAL PARTIES", value: "284" }]}/>
        <MCard title="Party Size Distribution" subtitle="Count and % share by group size">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {data.map((d, i) => {
              const pct = ((d.count / total) * 100).toFixed(0);
              return (
                <div key={i}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                    <span style={{ fontSize: 11, color: T.t2 }}>Party of {d.size}</span>
                    <span style={{ fontSize: 11, color: T.t1 }}><span style={{ fontWeight: 600 }}>{d.count}</span> <span style={{ color: T.t3 }}>({pct}%)</span></span>
                  </div>
                  <div style={{ height: 5, background: T.hover, borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: `${T.green}AA` }}/>
                  </div>
                </div>
              );
            })}
          </div>
        </MCard>
        <MCard title="Wait Time by Party Size" subtitle="Larger parties wait longer">
          <BarChart data={data.map(d => ({ s: `${d.size}`, w: d.wait }))} valueKey="w" labelKey="s" height={70} color={T.orange} threshold={30} />
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 8, justifyContent: "flex-end" }}>
            <div style={{ width: 10, height: 3, background: T.accent, borderRadius: 1 }}/>
            <span style={{ fontSize: 8, color: T.accent }}>Over 30 min</span>
          </div>
        </MCard>
        <MCard title="Dine-In Duration by Party Size" subtitle="Avg minutes at table">
          <BarChart data={data.map(d => ({ s: `${d.size}`, dur: d.dinein }))} valueKey="dur" labelKey="s" height={70} color={T.green} />
        </MCard>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DRILL-IN: CANCELLATIONS — by day, by hour, by channel
   ═══════════════════════════════════════════════════════════════════════════ */
function DrillCancels({ onBack }: any) {
  return (
    <>
      <DrillInHeader title="Cancellations" subtitle="35 cancellations (12.3% rate)" breadcrumb="Check-In Analytics › Cancellations" onBack={onBack} />
      <div style={{ flex: 1, overflow: "auto", padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        <SummaryKPIs items={[{ label: "TOTAL CANCELS", value: "35", color: T.red },{ label: "CANCEL RATE", value: "12.3%" },{ label: "CHANGE", value: "↓ 2.1%", color: T.green }]}/>
        <MCard title="Daily Cancellation Trend" subtitle="Cancellations per day with rate">
          <BarChart data={MOCK.cancelTrend.map(d => ({ d: d.d, v: d.cancels }))} valueKey="v" labelKey="d" height={70} color={T.red} />
          <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
            {MOCK.cancelTrend.map((d, i) => <div key={i} style={{ flex: 1, textAlign: "center", fontSize: 7, color: T.t3 }}>{d.rate}%</div>)}
          </div>
          <div style={{ textAlign: "center", fontSize: 7, color: T.t3, marginTop: 2 }}>daily cancel rate %</div>
        </MCard>
        <MCard title="Cancellations by Hour" subtitle="When do walk-aways happen?">
          <BarChart data={MOCK.cancelByHour} valueKey="c" labelKey="h" height={60} color={T.red} />
        </MCard>
        <MCard title="Cancellations by Channel" subtitle="Which channels lose the most?">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {MOCK.cancelByChannel.map((c, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 10 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: T.t1 }}>{c.name}</div>
                  <div style={{ fontSize: 9, color: T.t3, marginTop: 2 }}>Cancel rate: {c.rate}%</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: T.red }}>{c.cancels}</div>
                  <div style={{ fontSize: 8, color: T.t3 }}>cancellations</div>
                </div>
              </div>
            ))}
          </div>
        </MCard>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DRILL-IN: AVG WAIT TIME — by day, by party size, weekend vs weekday
   ═══════════════════════════════════════════════════════════════════════════ */
function DrillWaitTime({ onBack }: any) {
  return (
    <>
      <DrillInHeader title="Avg Wait Time" subtitle="18 min average this period" breadcrumb="Check-In Analytics › Wait Time" onBack={onBack} />
      <div style={{ flex: 1, overflow: "auto", padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        <SummaryKPIs items={[{ label: "AVG WAIT", value: "18m" },{ label: "SHORTEST DAY", value: "Mon 14m" },{ label: "LONGEST DAY", value: "Sat 28m" }]}/>
        <MCard title="Wait Time by Day of Week" subtitle="Avg minutes from check-in to seating">
          <BarChart data={MOCK.waitByDay} valueKey="wait" labelKey="d" height={80} color={T.orange} threshold={25} />
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 8, justifyContent: "flex-end" }}>
            <div style={{ width: 10, height: 3, background: T.accent, borderRadius: 1 }}/>
            <span style={{ fontSize: 8, color: T.accent }}>Over 25 min</span>
          </div>
        </MCard>
        <MCard title="Wait Time by Party Size" subtitle="Larger groups wait longer">
          <HProgressList data={MOCK.partySize.map(d => ({ label: `Party of ${d.size}`, wait: d.wait }))} labelKey="label" valueKey="wait" valueSuffix=" min" color={T.orange} />
        </MCard>
        <MCard title="Weekend vs Weekday" subtitle="Average wait comparison">
          <div style={{ display: "flex", gap: 8 }}>
            {[{ label: "Weekday", value: "16m", sub: "Mon–Fri avg" },{ label: "Weekend", value: "24m", sub: "Sat–Sun avg" }].map((it, i) => (
              <div key={i} style={{ flex: 1, textAlign: "center", padding: "14px 10px", background: i === 1 ? T.orangeSoft : T.hover, borderRadius: 10, border: `1px solid ${i === 1 ? `${T.orange}33` : T.cardBorder}` }}>
                <div style={{ fontSize: 8, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>{it.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: i === 1 ? T.orange : T.t1, marginTop: 4 }}>{it.value}</div>
                <div style={{ fontSize: 9, color: T.t3, marginTop: 2 }}>{it.sub}</div>
              </div>
            ))}
          </div>
        </MCard>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DRILL-IN: AVG DINE-IN — by party size, by day, weekend vs weekday
   ═══════════════════════════════════════════════════════════════════════════ */
function DrillDineIn({ onBack }: any) {
  return (
    <>
      <DrillInHeader title="Avg Dine-In Duration" subtitle="47 min average this period" breadcrumb="Check-In Analytics › Dine-In" onBack={onBack} />
      <div style={{ flex: 1, overflow: "auto", padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        <SummaryKPIs items={[{ label: "AVG DURATION", value: "47m" },{ label: "FASTEST SIZE", value: "Solo 32m" },{ label: "SLOWEST SIZE", value: "7+ at 78m" }]}/>
        <MCard title="Duration by Party Size" subtitle="Minutes at table per group size">
          <HProgressList data={MOCK.partySize.map(d => ({ label: `Party of ${d.size}`, dur: d.dinein }))} labelKey="label" valueKey="dur" valueSuffix=" min" color={T.green} />
        </MCard>
        <MCard title="Duration by Day of Week" subtitle="Avg dine-in minutes per day">
          <BarChart data={MOCK.dineinByDay} valueKey="dur" labelKey="d" height={80} color={T.green} />
        </MCard>
        <MCard title="Weekend vs Weekday" subtitle="Dine-in duration comparison">
          <div style={{ display: "flex", gap: 8 }}>
            {[{ label: "Weekday", value: "44m", sub: "Mon–Fri avg" },{ label: "Weekend", value: "52m", sub: "Sat–Sun avg" }].map((it, i) => (
              <div key={i} style={{ flex: 1, textAlign: "center", padding: "14px 10px", background: i === 1 ? T.greenSoft : T.hover, borderRadius: 10, border: `1px solid ${i === 1 ? `${T.green}33` : T.cardBorder}` }}>
                <div style={{ fontSize: 8, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>{it.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: i === 1 ? T.green : T.t1, marginTop: 4 }}>{it.value}</div>
                <div style={{ fontSize: 9, color: T.t3, marginTop: 2 }}>{it.sub}</div>
              </div>
            ))}
          </div>
        </MCard>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   APP — NAVIGATION CONTROLLER
   ═══════════════════════════════════════════════════════════════════════════ */
export default function CheckinReportMobile() {
  const [screen, setScreen] = useState("main");
  const goBack = () => setScreen("main");
  const drillMap = {
    checkins: <DrillCheckins onBack={goBack} />,
    guests:   <DrillGuests onBack={goBack} />,
    avgParty: <DrillAvgParty onBack={goBack} />,
    cancels:  <DrillCancels onBack={goBack} />,
    waitTime: <DrillWaitTime onBack={goBack} />,
    dinein:   <DrillDineIn onBack={goBack} />,
  };

  return (
    <div style={{
      width: 375, margin: "0 auto", background: T.bg, fontFamily: T.font,
      minHeight: "100vh", display: "flex", flexDirection: "column",
      WebkitFontSmoothing: "antialiased", borderRadius: 20,
      boxShadow: "0 0 40px rgba(0,0,0,0.12)", overflow: "hidden", position: "relative",
    }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=swap');
.material-symbols-rounded { font-family: 'Material Symbols Rounded'; font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24; }`}</style>
      <StatusBar />
      {screen === "main" ? <MainDashboard onDrill={setScreen} /> : drillMap[screen] || <MainDashboard onDrill={setScreen} />}
      <BottomNav />
    </div>
  );
}
